import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { AlertCircle, LogIn, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { ModuleIcon } from "@/components/ModuleIcon";

/**
 * Login page — allineata al Brand Book Gruppo Impresa Suite v1.0 (pag. 06).
 * Layout ufficiale: card centrata su sfondo neutro, header brand di prodotto,
 * icona modulo colorata, decorazione onde alla base, pulsante colorato full-width.
 * Identità cromatica di questo modulo: Turchese Suite #0D96BB.
 *
 * v1.60.4 — la username viene memorizzata in localStorage per pre-compilare
 * il campo agli accessi successivi. La password NON viene mai persistita
 * (nessun localStorage/sessionStorage/cookie applicativo).
 */
const REMEMBERED_USERNAME_KEY = "pgi:remembered_username";

export default function Login() {
  const { status, login, autoLogout, clearAutoLogout } = useAuth();
  const [username, setUsername] = useState(() => {
    try {
      return localStorage.getItem(REMEMBERED_USERNAME_KEY) || "";
    } catch {
      return "";
    }
  });
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const passwordRef = useRef(null);
  const usernameRef = useRef(null);

  useEffect(() => {
    // Password vuota ad ogni apertura — MAI persistita.
    setPassword("");
    // Se abbiamo già una username ricordata, focus diretto sulla password.
    if (username) {
      passwordRef.current?.focus();
    } else {
      usernameRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const trimmed = username.trim();
      await login(trimmed, password);
      // Persist SOLO la username (mai la password). Se il login fallisce,
      // NON salviamo nulla — la memoria resta quella dell'ultimo accesso
      // riuscito.
      try {
        localStorage.setItem(REMEMBERED_USERNAME_KEY, trimmed);
      } catch {
        /* storage disabilitato: silenzioso */
      }
    } catch (err) {
      let msg;
      if (!err?.response) {
        msg =
          "Impossibile contattare il server. Controlla la connessione e riprova.";
      } else if (err.response.status === 401) {
        const detail = err.response.data?.detail;
        msg = typeof detail === "string" ? detail : "Credenziali non valide.";
      } else {
        msg = `Errore ${err.response.status}. Riprova o contatta l'assistenza.`;
      }
      setError(msg);
      setPassword("");
      passwordRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "authenticated") return <Navigate to="/" replace />;

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-slate-50 px-4 py-10"
      data-testid="login-page"
    >
      {/* Background decorativo — accenti Suite */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-24 -bottom-24 h-[26rem] w-[26rem] rounded-full bg-primary/8 blur-3xl" />
      </div>

      {/* ---------------- CARD LOGIN ---------------- */}
      <div
        className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,42,74,0.12)]"
        data-testid="login-card"
      >
        <div className="px-8 pt-8 pb-6">
          {/* Header brand: nome della Suite in maiuscoletto colorato */}
          <div className="text-center">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-primary">
              Gruppo Impresa Suite
            </div>
          </div>

          {/* Icona modulo */}
          <div className="mt-6 flex justify-center">
            <ModuleIcon size={64} variant="solid" />
          </div>

          {/* Nome modulo */}
          <div className="mt-4 text-center">
            <h1 className="font-serif text-[22px] font-semibold leading-tight tracking-tight text-slate-900">
              Gestionale
              <br />
              Incassi &amp; Pagamenti
            </h1>
            <p className="mx-auto mt-2 max-w-[16rem] text-[11px] leading-snug text-slate-500">
              Controllo economico, flussi e liquidità in un unico ambiente della Suite.
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            autoComplete="off"
            className="mt-6 space-y-4"
            data-testid="login-form"
          >
            {autoLogout && (
              <div
                data-testid="autologout-banner"
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              >
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Sessione scaduta</div>
                  <div>
                    Disconnessione automatica dopo 10 minuti di inattività.
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div
                data-testid="login-error"
                className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
              >
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label
                htmlFor="username"
                className="text-[11px] font-medium uppercase tracking-wider text-slate-500"
              >
                Utente
              </Label>
              <Input
                id="username"
                ref={usernameRef}
                data-testid="login-username"
                type="text"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => {
                  if (autoLogout) clearAutoLogout();
                  setUsername(e.target.value);
                }}
                className="h-10"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="password"
                className="text-[11px] font-medium uppercase tracking-wider text-slate-500"
              >
                Password
              </Label>
              <Input
                id="password"
                ref={passwordRef}
                data-testid="login-password"
                type="password"
                autoComplete="new-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={password}
                onChange={(e) => {
                  if (autoLogout) clearAutoLogout();
                  setPassword(e.target.value);
                }}
                className="h-10"
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={submitting || !username || !password}
              className="mt-2 h-11 w-full gap-2 rounded-lg bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              <LogIn size={14} />
              {submitting ? "Accesso in corso…" : "Accedi"}
            </Button>

            <div className="flex items-center justify-center gap-1.5 pt-0.5 text-[10px] leading-tight text-slate-400">
              <ShieldCheck size={11} className="shrink-0 text-slate-400" />
              <span>
                La password non viene mai memorizzata · richiesta ad ogni accesso
              </span>
            </div>
          </form>
        </div>

        {/* Onda decorativa alla base (identità Suite) */}
        <div className="relative -mt-1 h-16">
          <svg
            viewBox="0 0 400 80"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
          >
            <path
              d="M0,50 C80,10 160,90 240,45 C320,10 380,60 400,45 L400,80 L0,80 Z"
              fill="hsl(var(--primary))"
              opacity="0.12"
            />
            <path
              d="M0,60 C90,25 180,80 260,55 C330,32 380,68 400,58 L400,80 L0,80 Z"
              fill="hsl(var(--primary))"
              opacity="0.22"
            />
            <path
              d="M0,72 C90,58 180,80 260,70 C330,62 380,78 400,72 L400,80 L0,80 Z"
              fill="hsl(var(--primary))"
              opacity="0.35"
            />
          </svg>
        </div>
      </div>

      {/* Corporate footer */}
      <div className="absolute bottom-6 left-0 right-0 z-10 flex items-center justify-center gap-2 text-[10px] tracking-wide text-slate-400">
        <BrandMark size={14} />
        <span>© Studio Bonaiuti · Gruppo Impresa STP a r.l.</span>
      </div>
    </div>
  );
}
